import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CalendarClock, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";

export default function Volunteer() {
  useSeo("Volunteer", "Volunteer with Golden Wolf Foundation — dog handling, landscaping, carpentry, mural art, photography, fundraising and more.");
  const [meta, setMeta] = useState({ skills: [], shifts: [] });
  const [selected, setSelected] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "", availability: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/volunteer/meta").then((r) => setMeta(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (s) => setSelected((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/applications/volunteer", { ...f, skills: selected }); toast.success("Thank you for volunteering! Check your email."); setF({ name: "", email: "", phone: "", availability: "" }); setSelected([]); }
    catch { toast.error("Submission failed"); } finally { setLoading(false); }
  };

  const signup = async (shift) => {
    if (!f.name || !f.email) return toast.error("Enter your name and email above first");
    try { await api.post(`/volunteer/shifts/${shift.id}/signup`, { name: f.name, email: f.email, phone: f.phone }); toast.success(`Signed up for ${shift.title}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not sign up"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <SectionHeader overline="Volunteer Portal" title="Lend Your Skills" sub="Whatever you're good at, the mission needs it. Pick your skills, apply, and claim a shift." />
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-sm text-[#C8C4B7] mb-2">Name *</label><input required className="gw-input" data-testid="vol-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div><label className="block text-sm text-[#C8C4B7] mb-2">Email *</label><input type="email" required className="gw-input" data-testid="vol-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              <div><label className="block text-sm text-[#C8C4B7] mb-2">Phone</label><input className="gw-input" data-testid="vol-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
              <div><label className="block text-sm text-[#C8C4B7] mb-2">Availability</label><input className="gw-input" data-testid="vol-availability" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} placeholder="Weekends, evenings…" /></div>
            </div>
            <div>
              <label className="block text-sm text-[#C8C4B7] mb-3">Skill categories</label>
              <div className="flex flex-wrap gap-2">
                {meta.skills.map((s) => (
                  <button type="button" key={s} onClick={() => toggle(s)} data-testid={`skill-${s.replace(/\s/g, "-").toLowerCase()}`}
                    className={`text-sm px-3 py-2 border transition-colors ${selected.includes(s) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-[#C8C4B7] hover:border-[#D4AF37]"}`}>
                    {selected.includes(s) && <Check className="w-3 h-3 inline mr-1" />}{s}
                  </button>
                ))}
              </div>
            </div>
            <button disabled={loading} className="btn-gold flex items-center justify-center gap-2 py-3.5 w-full" data-testid="vol-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Join as a Volunteer</button>
          </form>
        </div>

        <div>
          <div className="overline mb-4 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Open Shifts</div>
          <div className="space-y-3">
            {meta.shifts.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="gw-card p-5 flex items-center justify-between" data-testid={`shift-${s.id}`}>
                <div>
                  <h4 className="font-heading text-lg text-[#FDFBF7]">{s.title}</h4>
                  <p className="text-xs text-[#8A877B] mt-1">{s.date} · {s.time} · {s.skill}</p>
                  <p className="text-xs text-[#D4AF37] mt-1">{s.spots_left} of {s.capacity} spots left</p>
                </div>
                <button disabled={s.spots_left === 0} onClick={() => signup(s)} data-testid={`shift-signup-${s.id}`}
                  className={`text-sm px-4 py-2 transition-colors ${s.spots_left === 0 ? "border border-white/10 text-[#8A877B] cursor-not-allowed" : "btn-outline"}`}>
                  {s.spots_left === 0 ? "Full" : "Sign Up"}
                </button>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-[#8A877B] mt-4">Automated email and text reminders are sent before each shift.</p>
        </div>
      </div>
    </div>
  );
}
